/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import * as d3 from 'd3';
import { calculateThetaFromPressTemp, pressToPascal } from './bijvoetUtils';
import type { SoundingDiagramType } from '../../api/edr-api/types';
import {
  EPSILON,
  KELVIN,
  REFERENCE_PRESSURE,
  WV_CA,
  WV_CB,
  WV_CE,
} from '../Soundings/constants';
import type { ScalesType } from '../Soundings/types';

const isohumeValues = [
  0.001, 0.01, 0.1, 0.5, 1.0, 2.0, 5.0, 8.0, 12.0, 16.0, 20.0,
];

interface DrawIsohumesProps {
  staticPressureArray: number[];
  height: number;
  xScale: ScalesType;
  yScale: ScalesType;
  svg: d3.Selection<SVGElement, unknown, HTMLElement, unknown>;
  diagramType: SoundingDiagramType;
  d3GraphId: string;
}

export const drawIsohumes = ({
  staticPressureArray,
  height,
  xScale,
  yScale,
  svg,
  diagramType,
  d3GraphId,
}: DrawIsohumesProps): void => {
  for (const isohumeVal of isohumeValues) {
    const Px = [];

    for (const pressureVal of staticPressureArray) {
      const es = (pressureVal * isohumeVal) / (isohumeVal + 622.0);
      const logthing = Math.log(es / 6.11);
      const temp = (237.3 * logthing) / (17.269 - logthing);
      Px.push([xScale(temp), yScale(pressureVal)]);
    }

    if (diagramType === 'SKEWT') {
      // Skew/shear transformation for skew-t
      for (const PxElement of Px) {
        PxElement[0] = PxElement[0] + height - PxElement[1];
      }
    }

    const lineGenerator = d3.line();
    const pathStringQ = lineGenerator(Px as [number, number][]);

    const strID = `${isohumeVal}isohume`;

    svg
      .append('path')
      .attr('id', strID)
      .attr('d', pathStringQ)
      .style('fill', 'none')
      .style('stroke-width', 1)
      .style('stroke-dasharray', '15, 8')
      .style('stroke', '#5267A9')
      .style('stroke-opacity', 0.5)
      .attr('clip-path', `url(#clip-${d3GraphId}-${diagramType}d3)`);

    svg
      .append('text')
      .text(isohumeVal)
      .attr('text-anchor', 'middle')
      .attr('x', `${Px[0][0] + 20}px`)
      .attr('y', `${height - 10}px`)
      .attr('fill', '#5267A9')
      .attr('font-size', '10px');
  }
};

const isohumeValuesBijvoet = [1, 2, 4, 7, 11, 16, 22, 29];

interface DrawIsohumesBijvoetProps {
  staticPressureArray: number[];
  height: number;
  xScale: ScalesType;
  yScale: ScalesType;
  svg: d3.Selection<SVGElement, unknown, HTMLElement, unknown>;
  d3GraphId: string;
}

const mixr2Tdew = (mixr: number, pressure: number): number => {
  const mixingRation = mixr < 1e-6 ? 1e-6 : mixr;

  const eVap = (pressure * mixingRation) / (EPSILON + mixingRation);
  return WV_CB / (WV_CA / Math.log(eVap / WV_CE) - 1.0) + KELVIN;
};

export const drawIsohumesBijvoet = ({
  staticPressureArray,
  height,
  xScale,
  yScale,
  svg,
  d3GraphId,
}: DrawIsohumesBijvoetProps): void => {
  for (const isohumeVal of isohumeValuesBijvoet) {
    const Px = [];

    const mixr = isohumeVal * 1e-3;

    for (const pressureVal of staticPressureArray) {
      const pres = pressToPascal(pressureVal); // Pressure in Pa
      const Tdew = mixr2Tdew(mixr, pres);
      const theta = calculateThetaFromPressTemp(pres, Tdew, REFERENCE_PRESSURE);
      Px.push([xScale(theta), yScale(pressureVal)]);
    }

    const lineGenerator = d3.line();
    const pathStringQ = lineGenerator(Px as [number, number][]);

    const strID = `${isohumeVal}isohume`;

    svg
      .append('path')
      .attr('id', strID)
      .attr('d', pathStringQ)
      .style('fill', 'none')
      .style('stroke-width', 1)
      .style('stroke-dasharray', '15, 8')
      .style('stroke', '#5267A9')
      .style('stroke-opacity', 0.5)
      .attr('clip-path', `url(#clip-${d3GraphId}-BIJVOETd3)`);

    svg
      .append('text')
      .text(isohumeVal)
      .attr('text-anchor', 'middle')
      .attr('x', `${Px[0][0] + 20}px`)
      .attr('y', `${height - 10}px`)
      .attr('fill', '#5267A9')
      .attr('font-size', '10px');
  }
};
